/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericSwitch from "@ui-components/Switch/GenericSwitch";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
	getListSolicitud,
	getListTwoLevel,
	createTicket,
	createKontrolTicket,
	CreateTicketPayload,
	getPortsAndLag,
} from "../request/request";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import { getServiceLabel } from "../GetServiceLabel";
import { ServiceItem, ServiceType } from "../Types";

interface LabelValue {
	label: string;
	value: string;
}

interface ChangeRequestModalProps {
	isOpen: boolean;
	onClose: () => void;
	onTicketCreated?: () => void;
}

const productData: LabelValue[] = [
	{ label: "CTX Reflector", value: "ctx-r" },
	{ label: "Kontrol Edge", value: "ke" },
];

const countryCodes: LabelValue[] = [
	{ label: "United States (+1)", value: "+1" },
	{ label: "Mexico (+52)", value: "+52" },
	{ label: "Canada (+1)", value: "+1" },
];

const TABS = [
	{ id: "serviceSelection", label: "Service selection" },
	{ id: "requestInfo", label: "Request Info" },
	{ id: "attachments", label: "Attachments & Response" },
];

// Ajustamos aquí, en lugar de "Low, Medium, High"
// usaremos las mismas opciones que en tu select nativo: Low, Medium, High, Urgent
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

export const ChangeRequestModal: React.FC<ChangeRequestModalProps> = ({
	isOpen,
	onClose,
	onTicketCreated,
}) => {
	const api = useAxiosAuth0();
	const { tenantId } = useAppSelector((state) => state.selection);
	const [selectedProductLabel] = useState("");
	const [topics, setTopics] = useState<LabelValue[]>([]);
	const [selectedTopicLabel, setSelectedTopicLabel] = useState("");
	const [twoLevelOptions, setTwoLevelOptions] = useState<LabelValue[]>([]);
	const [selectedTwoLevelLabel, setSelectedTwoLevelLabel] = useState("");
	const [cloud] = useState("CloudGlobal");
	const [region] = useState("us-east-1");
	const [title, setTitle] = useState("");
	const [priority, setPriority] = useState("");
	const [description, setDescription] = useState("");
	const [sendResponse, setSendResponse] = useState(false);
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [selectedCountryCode, setSelectedCountryCode] = useState("+1");
	const [contactPhoneNumber, setContactPhoneNumber] = useState("");
	const [attachmentFiles, setAttachmentFiles] = useState<FileList | null>(null);
	const [loadingTopics, setLoadingTopics] = useState(false);
	const [loadingTwoLevels, setLoadingTwoLevels] = useState(false);
	const [activeTab, setActiveTab] = useState<string>("serviceSelection");
	const [isSaving, setIsSaving] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertVariant, setAlertVariant] = useState<"success" | "error">(
		"success"
	);
	const [alertMessage, setAlertMessage] = useState("");
	const [serviceItems, setServiceItems] = useState<ServiceItem[]>([]);
	const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
		null
	);
	const [selectedServiceType, setSelectedServiceType] = useState<
    "port" | "internet" | "dci" | "cloud" | "fwaas" | null
	>(null);
	const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null); //
	const [selectedServiceLabel, setSelectedServiceLabel] = useState<string>("");


	const isLoadingSomething = loadingTopics || loadingTwoLevels || isSaving;
	const overlay = useProcessingOverlay(isLoadingSomething);
	// Check if the selected product is "Kontrol Edge"
	const globalProduct = useAppSelector(
		(state) => state.product.selectedProduct
	);
	const isKontrolEdge = globalProduct === "Kontrol Edge";

	const stripNonAlphanumeric = (str: string) => str.replace(/[\s\W_]+/g, "");
	const isValidEmail = (email: string) =>
		/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

	const resetFormState = () => {
		setTopics([]);
		setSelectedTopicLabel("");
		setTwoLevelOptions([]);
		setSelectedTwoLevelLabel("");
		setTitle("");
		setPriority("");
		setDescription("");
		setSendResponse(false);
		setContactName("");
		setContactEmail("");
		setSelectedCountryCode("+1");
		setContactPhoneNumber("");
		setAttachmentFiles(null);
		setActiveTab("serviceSelection");
		setSelectedSubscriptionId(null); //
	};


	useEffect(() => {
		if (!isKontrolEdge) {
		setServiceItems([]);
		setSelectedServiceId(null);
		setSelectedServiceType(null);
		return;
	}

	function toServiceItems<T extends { id: number; subscriptionId: string; name?: string; tenant?: { id: number; name: string; nameDisplay?: string } }>(
		arr: T[] | unknown,
		type: ServiceType,
		fallbackPrefix: string
	): ServiceItem[] {
	if (!Array.isArray(arr)) return [];
	return arr.map(({ id, subscriptionId, name, customer  }) => ({
		id,
		subscriptionId,
		name: name ?? `${fallbackPrefix}-${id}`,
		type,
		customer: customer
		? { id: customer.id, name: customer.name, nameDisplay: customer.nameDisplay }
		: undefined,
	}));
	}

	
	const fetchData = async () => {
    try {
      const resp = await getPortsAndLag(api, tenantId);
      const data = resp?.data?.data ?? {};

      const combined: ServiceItem[] = [
        ...toServiceItems(data.ports, "port", "Port"),
        ...toServiceItems(data.internet, "internet", "Internet"),
        ...toServiceItems(data.datacenterInterconnect, "dci", "DCI"),
        ...toServiceItems(data.cloudConnectivity, "cloud", "Cloud"),
		...toServiceItems(data.fwaas, "fwaas", "FWaaS"),
      ];

      setServiceItems(combined);
    } catch (error) {
      console.error("Error fetching ports/lags =>", error);
    }
  };
	
	fetchData();
	}, [isKontrolEdge, api, tenantId, isOpen]);


	const handleCloseModal = () => {
		resetFormState();
		onClose();
	};

	const fetchTopics = async (productValue: string) => {
		try {
			setLoadingTopics(true);
			const resp = await getListSolicitud(api, "Change Request", productValue);
			setTopics(resp.data as LabelValue[]);
		} catch (error) {
			console.error("Error fetching topics =>", error);
		} finally {
			setLoadingTopics(false);
		}
	};

	const fetchTwoLevels = async (productValue: string, topicLabel: string) => {
		try {
			setLoadingTwoLevels(true);
			const resp = await getListTwoLevel(
				api,
				"Change Request",
				productValue,
				topicLabel
			);
			setTwoLevelOptions(resp.data as LabelValue[]);
		} catch (error) {
			console.error("Error fetching two-level =>", error);
		} finally {
			setLoadingTwoLevels(false);
		}
	};
	useEffect(() => {
		if (!isKontrolEdge) {
			// For non-Kontrol Edge, fetch topics if needed.
			const found = productData.find((p) => p.label === globalProduct);
			if (found) {
				fetchTopics(found.value);
			}
		}
	}, [globalProduct, isKontrolEdge]);

	const handleSelectTopic = (topicLabel: string) => {
		setSelectedTopicLabel(topicLabel);
		setTwoLevelOptions([]);
		setSelectedTwoLevelLabel("");
		const productFound = productData.find((p) => p.label === globalProduct);
		if (productFound) {
			fetchTwoLevels(productFound.value, topicLabel);
		}
	};

	const handleSelectTwoLevel = (twoLabel: string) => {
		setSelectedTwoLevelLabel(twoLabel);
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) {
			setAttachmentFiles(e.target.files);
		}
	};
	function isGeneralInfoValid() {
		// For Kontrol Edge, skip topic/request validation
		if (isKontrolEdge) {
			return true;
		}
		// Otherwise require topic and request
		return (
			selectedTopicLabel.trim() !== "" && selectedTwoLevelLabel.trim() !== ""
		);
	}

	const isAdditionalInfoValid = () => {
		// For Kontrol Edge, we hide these fields so we automatically consider them valid.
		if (isKontrolEdge) return priority.trim() !== "";
		const titleClean = stripNonAlphanumeric(title);
		const descClean = stripNonAlphanumeric(description);
		return (
			cloud.trim() !== "" &&
			region.trim() !== "" &&
			titleClean.length >= 5 &&
			descClean.length >= 10 &&
			priority.trim() !== ""
		);
	};

	const isAttachmentsValid = () => {
		if (isKontrolEdge) return true;
		if (!sendResponse) return true;
		const minName = contactName.trim().length >= 3;
		const validEmail = isValidEmail(contactEmail.trim());
		return minName && validEmail;
	};

	const canActivateTab = (tabId: string) => {
		if (tabId === "serviceSelection") return true;
		if (tabId === "requestInfo") return isGeneralInfoValid();
		if (tabId === "attachments")
			return isGeneralInfoValid() && isAdditionalInfoValid();
		return false;
	};

	const handleChangeTab = (tabId: string) => {
		if (canActivateTab(tabId)) {
			setActiveTab(tabId);
		}
	};
	
	const getSelectedServiceName = () => {
		const selectedItem = serviceItems.find(
			(item) =>
				item.id === selectedServiceId && item.type === selectedServiceType
		);
		return selectedItem ? getServiceLabel(selectedItem) : "";
	};

	const handleSave = async () => {
		if (!isAttachmentsValid()) return;
		setIsSaving(true);
		try {
			const foundProduct = productData.find(
				(p) => p.label === selectedProductLabel
			);
			const productValue = foundProduct ? foundProduct.value : "";
			const foundTopic = topics.find((t) => t.label === selectedTopicLabel);
			const topicValue = foundTopic ? foundTopic.value : "";
			const foundTwoLevel = twoLevelOptions.find(
				(t) => t.label === selectedTwoLevelLabel
			);
			const twoLevelValue = foundTwoLevel ? foundTwoLevel.value : "";

			// Priority mapping
			let priorityNumber = 4;
			if (priority === "Low") priorityNumber = 4;
			if (priority === "Medium") priorityNumber = 3;
			if (priority === "High") priorityNumber = 2;
			if (priority === "Urgent" && isKontrolEdge) priorityNumber = 10000;

			if (isKontrolEdge) {
				if (!selectedSubscriptionId || !selectedServiceType) {
					setAlertVariant("error");
					setAlertMessage("Please select a service before creating the ticket.");
					setAlertOpen(true);
					setIsSaving(false);
					return;
				}
				// ========== If product is Kontrol Edge => use createKontrolTicket + minimal fields ==========
				const kontrolPayload = {
					requesttype: "CHANGE_REQUEST",
					summary: title,
					priority: priorityNumber,
					description,
					subscriptionID: selectedSubscriptionId,
					serviceType: selectedServiceType,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
				};
				// console.log("Kontrol ticket created => kontrolPayload", kontrolPayload);
				const response = await createKontrolTicket(api, kontrolPayload);
				if (onTicketCreated) {
					onTicketCreated();
				}
				console.log("Kontrol ticket created =>", response.data);
			} else {
				// ========== Otherwise => use createTicket + all the normal fields ==========

				const finalPayload: CreateTicketPayload = {
					Tipo: "Change Request",
					product: productValue,
					cloud,
					region,
					TipoSolicitud: topicValue,
					level_3: twoLevelValue,
					Resumen: title,
					Prioridad: priorityNumber,
					Descripcion: description,
					files: attachmentFiles ? Array.from(attachmentFiles) : [],
				};
				if (sendResponse) {
					finalPayload.Nombre_de_contacto = contactName;
					finalPayload.Correo = contactEmail;
					finalPayload.phone_number = `${selectedCountryCode}${contactPhoneNumber.replace(
						/\s+/g,
						""
					)}`;
				}
				// console.log("Kontrol ticket created => finalPayload", finalPayload);
				await createTicket(api, finalPayload);
			}

			setAlertVariant("success");
			setAlertMessage("¡Ticket created successfully!");
			setAlertOpen(true);
		} catch (err) {
			console.error("Error creating ticket =>", err);
			setAlertVariant("error");
			setAlertMessage(
				"There was a problem creating the ticket, please try again."
			);
			setAlertOpen(true);
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<GenericModal
			isOpen={isOpen}
			onClose={handleCloseModal}
			closeOnBackdropClick={false}
			size="lg"
			title={
				<div className="flex items-center gap-2">
					<span>Change Request</span>
					<GenericTooltip
						text="Use this section to tell us about your Change requests."
						position="top"
					/>
				</div>
			}
		>
			<div className="border-b border-gray-200 mb-4">
				<nav className="flex space-x-4" aria-label="Tabs">
					{TABS.map((tab) => {
						const disabled = !canActivateTab(tab.id);
						return (
							<button
								key={tab.id}
								onClick={() => handleChangeTab(tab.id)}
								disabled={disabled}
								className={`
                  px-3 py-2 text-sm font-medium focus:outline-none
                  ${
										activeTab === tab.id
											? "text-blue-600 border-b-2 border-blue-600"
											: disabled
											? "text-gray-400 cursor-not-allowed"
											: "text-gray-500 hover:text-gray-700"
									}
                `}
							>
								{tab.label}
							</button>
						);
					})}
				</nav>
			</div>

			{activeTab === "serviceSelection" && (
				<div className="space-y-4 text-sm text-gray-700 ">
					<div className="flex gap-4">
						{/* If Kontrol Edge => show the new dropdown */}
						{isKontrolEdge && (
							<div className="w-1/2">
								<label className="block mb-1 font-medium">Select Service</label>
								<GenericDropdown
									items={serviceItems.map(getServiceLabel)}
									value={getSelectedServiceName()}
									onSelect={(selectedLabel: string) => {
										const selected = serviceItems.find(
										(item) => getServiceLabel(item) === selectedLabel
										);
										setSelectedServiceId(selected?.id ?? null);
										setSelectedServiceType(selected?.type ?? null);
										setSelectedServiceLabel(selectedLabel);
										setSelectedSubscriptionId(selected?.subscriptionId ?? null);
									}}
									defaultLabel="Select a service"
									minWidthClass=""
									maxWidthClass=""
									widthClass="w-full"
									dropdownMaxHeightClass="max-h-48"
								/>
							</div>
						)}
					</div>

					{!isKontrolEdge && (
						<>
							<div className="flex gap-4">
								<div className="w-1/2">
									<label className="block mb-1 font-medium">
										Topic <span className="text-red-500">*</span>
									</label>
									<GenericDropdown
										items={topics.map((t) => t.label)}
										value={selectedTopicLabel}
										onSelect={handleSelectTopic}
										defaultLabel="No options available"
										minWidthClass=""
										maxWidthClass=""
										widthClass="w-full"
									/>
								</div>
								<div className="w-1/2">
									<label className="block mb-1 font-medium">
										Specify your request <span className="text-red-500">*</span>
									</label>
									<GenericDropdown
										items={twoLevelOptions.map((t) => t.label)}
										value={selectedTwoLevelLabel}
										onSelect={handleSelectTwoLevel}
										defaultLabel="No options available"
										minWidthClass=""
										maxWidthClass=""
										widthClass="w-full"
									/>
								</div>
							</div>
						</>
					)}
					<div className="flex justify-end pt-2">
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("requestInfo")}
							disabled={!isGeneralInfoValid()}
						/>
					</div>
				</div>
			)}

			{activeTab === "requestInfo" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div className="flex gap-4">
						<div className="w-1/2">
							<label className="block mb-1 font-medium">
								Priority <span className="text-red-500">*</span>
							</label>
							<GenericDropdown
								items={PRIORITY_OPTIONS}
								value={priority}
								onSelect={(val) => setPriority(val)}
								defaultLabel="Select Priority"
								minWidthClass=""
								maxWidthClass=""
								widthClass="w-full"
							/>
						</div>
					</div>

					{!isKontrolEdge && (
						<>
							<div className="flex gap-4">
								<div className="w-1/2">
									<label className="block mb-1 font-medium">
										Cloud <span className="text-red-500">*</span>
									</label>
									<GenericInput value={cloud} disabled />
								</div>
								<div className="w-1/2">
									<label className="block mb-1 font-medium">
										Region <span className="text-red-500">*</span>
									</label>
									<GenericInput value={region} disabled />
								</div>
							</div>
						</>
					)}

					<div>
						<label className=" mb-1 font-medium flex items-center gap-1">
							Title <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 5 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<GenericInput
							placeholder="Write a brief description"
							value={title}
							onChange={setTitle}
						/>
					</div>

					<div>
						<label className=" mb-1 font-medium flex items-center gap-1">
							Description <span className="text-red-500">*</span>
							<GenericTooltip
								text="At least 10 characters (ignoring spaces & punctuation)."
								position="top"
							/>
						</label>
						<textarea
							className="w-full h-24 px-3 py-2 border border-gray-300 rounded-sm text-sm"
							placeholder="Description of the problem (required)"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
						/>
					</div>

					<div className="flex justify-end pt-2 gap-2">
						<GenericButton
							label="Back"
							variant="secondary"
							onClick={() => setActiveTab("serviceSelection")}
						/>
						<GenericButton
							label="Next"
							variant="brand"
							onClick={() => handleChangeTab("attachments")}
							disabled={!isAdditionalInfoValid()}
						/>
					</div>
				</div>
			)}

			{activeTab === "attachments" && (
				<div className="space-y-4 text-sm text-gray-700">
					<div>
						<label className="block mb-1 font-medium">
							Attach (Drag and drop screenshots or browse your device)
						</label>
						<div className="flex items-center gap-2">
							<label
								htmlFor="file-upload"
								className="
                  cursor-pointer 
                  px-3 py-2 
                  border border-gray-300 
                  rounded-sm 
                  bg-gray-50
                  text-sm 
                  hover:bg-gray-100
                "
							>
								{attachmentFiles ? "Change Files" : "Choose Files"}
							</label>
							<input
								id="file-upload"
								type="file"
								className="hidden"
								multiple
								onChange={handleFileChange}
							/>
							<span className="text-sm text-gray-500">
								{attachmentFiles
									? Array.from(attachmentFiles)
											.map((file) => file.name)
											.join(", ")
									: "No file chosen"}
							</span>
						</div>
					</div>
					{!isKontrolEdge && (
						<div className="space-y-2">
							<div className="flex items-center justify-between mt-3">
								<span className="font-medium text-sm text-gray-800">
									I want to send a response of the case to another person
								</span>
								<GenericSwitch
									checked={sendResponse}
									onChange={setSendResponse}
								/>
							</div>

							{sendResponse && (
								<div className="space-y-4 mt-2 border border-gray-200 rounded p-3">
									<div className="flex gap-4">
										<div className="w-1/2">
											<label className="block mb-1 font-medium flex items-center gap-1">
												Contact Name <span className="text-red-500">*</span>
												<GenericTooltip
													text="At least 3 characters."
													position="top"
												/>
											</label>
											<GenericInput
												placeholder="Write the name here"
												value={contactName}
												onChange={setContactName}
											/>
										</div>
										<div className="w-1/2">
											<label className="block mb-1 font-medium flex items-center gap-1">
												Contact Email <span className="text-red-500">*</span>
												<GenericTooltip
													text="Must be a valid email format (e.g. name@domain.com)."
													position="top"
												/>
											</label>
											<GenericInput
												placeholder="Write the email here"
												value={contactEmail}
												onChange={setContactEmail}
												type="email"
											/>
										</div>
									</div>

									<div className="flex gap-4">
										<div className="w-1/2">
											<label className="block mb-1 font-medium">
												Country Code
											</label>
											<GenericDropdown
												items={countryCodes.map((c) => c.label)}
												value={
													countryCodes.find(
														(c) => c.value === selectedCountryCode
													)?.label || "United States (+1)"
												}
												onSelect={(label) => {
													const found = countryCodes.find(
														(c) => c.label === label
													);
													if (found) {
														setSelectedCountryCode(found.value);
													}
												}}
												defaultLabel="Select country code"
												minWidthClass=""
												maxWidthClass=""
												widthClass="w-full"
											/>
										</div>
										<div className="w-1/2">
											<label className="block mb-1 font-medium">
												Phone Number
											</label>
											<GenericInput
												placeholder="Phone number"
												value={contactPhoneNumber}
												onChange={setContactPhoneNumber}
											/>
										</div>
									</div>
								</div>
							)}
						</div>
					)}

					<div className="flex justify-end pt-2 gap-2">
						<GenericButton
							label="Back"
							variant="secondary"
							onClick={() => setActiveTab("requestInfo")}
						/>
						<GenericButton
							label="Save"
							variant="brand"
							onClick={handleSave}
							disabled={!isAttachmentsValid() || isSaving}
						/>
					</div>
				</div>
			)}

			{overlay}

			<GenericAlert
				isOpen={alertOpen}
				onClose={() => setAlertOpen(false)}
				onConfirm={() => {
					setAlertOpen(false);
					if (alertVariant === "success") {
						handleCloseModal();
					}
				}}
				variant={alertVariant}
				title={alertVariant === "success" ? "Ticket Created" : "Error"}
				description={alertMessage}
				confirmLabel="OK"
			/>
		</GenericModal>
	);
};

export default ChangeRequestModal;
